import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { CONTACT, SERVICES, WHATSAPP_DEFAULT } from "@/data/content";

const navLinkClass = ({ isActive }) =>
  `text-sm font-semibold transition-colors duration-200 hover:text-amber-600 ${
    isActive ? "text-amber-600" : "text-navy"
  }`;

export const Logo = ({ dark = false }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center group">
    <span className={`inline-flex items-center ${dark ? "rounded-xl bg-white px-2.5 py-1.5" : ""}`}>
      <img src="/assets/logo.png" alt="NextMove Removalists" className={`w-auto transition-transform duration-300 group-hover:scale-[1.03] ${dark ? "h-11" : "h-12 sm:h-14 -my-1"}`} />
    </span>
  </Link>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200/60 shadow-sm"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8">
        <div className="flex items-center gap-4">
          <Logo />
          <span className="hidden lg:inline-flex items-center rounded-full border border-amber-500/40 bg-amber-50 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-amber-700">
            Fully Insured · Australia-Wide
          </span>
        </div>

        <nav className="hidden md:flex items-center gap-7" data-testid="desktop-nav">
          <div className="relative group">
            <button
              data-testid="nav-services-dropdown"
              className="flex items-center gap-1.5 text-sm font-semibold leading-none text-navy hover:text-amber-600 transition-colors duration-200"
            >
              <span>Services</span> <ChevronDown className="h-4 w-4 shrink-0 translate-y-[1px] transition-transform duration-200 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-0 top-full pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div className="w-64 rounded-xl border border-slate-200 bg-white/95 backdrop-blur-xl p-2 shadow-xl">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`nav-service-${s.slug}`}
                    className="block rounded-lg px-4 py-2.5 text-sm font-semibold text-navy hover:bg-amber-50 hover:text-amber-700 transition-colors duration-150"
                  >
                    {s.title}
                  </Link>
                ))}
                <Link
                  to="/services"
                  data-testid="nav-service-all"
                  className="block rounded-lg px-4 py-2.5 text-sm font-bold text-amber-600 hover:bg-amber-50 transition-colors duration-150"
                >
                  All Services →
                </Link>
              </div>
            </div>
          </div>
          <NavLink to="/areas" data-testid="nav-areas-link" className={navLinkClass}>Areas We Service</NavLink>
          <NavLink to="/about" data-testid="nav-about-link" className={navLinkClass}>Why Choose Us</NavLink>
          <NavLink to="/contact" data-testid="nav-contact-link" className={navLinkClass}>Contact</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${CONTACT.phonePrimaryRaw}`}
            data-testid="nav-call-btn"
            className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-bold text-navy hover:border-amber-500 hover:text-amber-600 transition-colors duration-200"
          >
            <Phone className="h-4 w-4" /> {CONTACT.phonePrimary}
          </a>
          <button
            onClick={() => navigate("/quote")}
            data-testid="nav-quote-btn"
            className="cta-gleam rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white"
          >
            Get Fast Quote
          </button>
        </div>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="md:hidden rounded-lg p-2 text-navy"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-slate-200 bg-white px-6 py-6 space-y-4">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Services</p>
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setOpen(false)}
              data-testid={`mobile-service-${s.slug}`}
              className="block text-base font-semibold text-navy">{s.title}</Link>
          ))}
          <div className="h-px bg-slate-200" />
          <Link to="/areas" onClick={() => setOpen(false)} data-testid="mobile-areas-link" className="block text-base font-semibold text-navy">Areas We Service</Link>
          <Link to="/about" onClick={() => setOpen(false)} data-testid="mobile-about-link" className="block text-base font-semibold text-navy">Why Choose Us</Link>
          <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-contact-link" className="block text-base font-semibold text-navy">Contact</Link>
          <div className="flex gap-3 pt-2">
            <a href={`tel:${CONTACT.phonePrimaryRaw}`} data-testid="mobile-call-btn"
              className="flex-1 rounded-full border border-navy px-4 py-3 text-center text-sm font-bold text-navy">
              Call {CONTACT.phonePrimary}
            </a>
            <Link to="/quote" onClick={() => setOpen(false)} data-testid="mobile-quote-btn"
              className="flex-1 rounded-full bg-navy px-4 py-3 text-center text-sm font-bold text-amber-400">
              Get Quote
            </Link>
          </div>
          <a href={WHATSAPP_DEFAULT} target="_blank" rel="noopener noreferrer" data-testid="mobile-whatsapp-btn"
            className="block rounded-full bg-[#25D366] px-4 py-3 text-center text-sm font-bold text-white">
            WhatsApp Us
          </a>
        </div>
      )}
    </header>
  );
}
